<template>
  <div
    class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-6 flex flex-col md:flex-row md:items-center gap-6 md:gap-7"
  >
    <!-- Left: name + score + network rank -->
    <div class="md:w-52">
      <p class="text-lg font-bold">{{ seller.name }}</p>
      <div class="flex items-baseline gap-1.5 mt-2">
        <span class="text-3xl font-extrabold tracking-tight leading-none">{{ seller.score }}</span>
        <span class="text-sm text-gray-500">
          {{ t(`challenge.counting.${countingMethod}.unit`, seller.score || 0) }}
        </span>
      </div>
      <p v-if="seller.networkRank != null" class="text-xs text-gray-500 mt-1">
        {{ t("network_rank", { rank: seller.networkRank, total: seller.totalNetworkSellers }) }}
      </p>
    </div>

    <!-- Middle: tiered progress -->
    <div v-if="hasSalesTargets" class="flex-1 min-w-0">
      <div class="flex items-center justify-between mb-3">
        <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">{{ t("tiers_label") }}</p>
        <p v-if="nextTarget" class="text-sm text-gray-600">
          {{
            t("next_tier", {
              remaining,
              unit: remainingUnit,
              target: nextTarget.target,
              reward: tierReward(nextTarget),
            })
          }}
        </p>
        <p v-else class="text-sm text-green-600 font-semibold">{{ t("all_tiers_reached") }}</p>
      </div>

      <div class="relative" :style="tierGridStyle">
        <!-- Track line (gray background) -->
        <div class="absolute left-0 right-0 top-[9px] h-0.5 bg-gray-200"></div>
        <!-- Filled line (primary progress) -->
        <div
          class="absolute left-0 top-[9px] h-0.5 bg-primary"
          style="transition: width 0.4s"
          :style="{ width: progressPercent + '%' }"
        ></div>

        <!-- Tier markers -->
        <div
          v-for="tier in sortedTargets"
          :key="tier.target"
          class="relative min-w-0 flex flex-col items-center gap-1.5 break-words"
        >
          <!-- Dot: reached -->
          <div v-if="isReached(tier)" class="size-[18px] rounded-full z-10 bg-primary"></div>
          <!-- Dot: next (ring + glow) -->
          <div
            v-else-if="isNext(tier)"
            class="rounded-full z-10 border-primary"
            style="
              width: 18px;
              height: 18px;
              background: white;
              border-width: 3px;
              box-shadow: 0 0 0 5px rgba(180, 108, 254, 0.18);
            "
          ></div>
          <!-- Dot: future -->
          <div v-else class="size-[18px] rounded-full z-10 bg-white border-2 border-gray-300"></div>

          <!-- Target count -->
          <span :class="tierTargetClass(tier)" class="text-xs whitespace-nowrap">{{ tier.target }}</span>
          <!-- Reward: a labelled one (e.g. a trip) wraps under its marker -->
          <span
            :class="[tierRewardClass(tier), tier.label ? 'text-center leading-tight' : 'whitespace-nowrap']"
            class="text-[11px]"
          >
            {{ tierReward(tier) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Right: gains -->
    <div class="md:w-64">
      <div class="rounded-xl py-3 px-4 border bg-primary-light border-primary-lighter text-primary-dark">
        <p class="text-[11px] font-semibold uppercase tracking-wide opacity-75">{{ t("earned_label") }}</p>
        <div class="flex items-baseline flex-wrap gap-x-2">
          <span class="font-extrabold tracking-tight leading-tight" :class="rewardIsLabel ? 'text-base' : 'text-3xl'">
            {{ earnedReward }}
          </span>
          <span v-if="nextTarget" class="text-xs">{{ t("max_reward", { max: tierReward(topTier) }) }}</span>
        </div>
      </div>

      <TieredChallengeSellerCompliance
        v-if="isGrossSales"
        class="mt-3"
        :score="seller.score || 0"
        :leads-count="seller.leadsCount || 0"
      />
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import {
  isGrossSalesCounting,
  nextTier,
  reachedTier,
  sellerReward,
  sortTiers,
  tierReward,
} from "@/utils/challengeTiers";
import TieredChallengeSellerCompliance from "@/components/challenge/tiered/TieredChallengeSellerCompliance.vue";

export default {
  components: { TieredChallengeSellerCompliance },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    seller: {
      type: Object,
      required: true,
    },
    salesTargets: {
      type: Array,
      required: true,
    },
    countingMethod: {
      type: String,
      default: "plan_sales",
    },
  },

  computed: {
    isGrossSales() {
      return isGrossSalesCounting(this.countingMethod);
    },

    hasSalesTargets() {
      return this.salesTargets && this.salesTargets.length > 0;
    },

    sortedTargets() {
      return sortTiers(this.salesTargets);
    },

    topTier() {
      return this.sortedTargets[this.sortedTargets.length - 1] ?? null;
    },

    nextTarget() {
      return nextTier(this.salesTargets, this.seller.score);
    },

    remaining() {
      if (!this.nextTarget) return 0;
      return this.nextTarget.target - (this.seller.score || 0);
    },

    remainingUnit() {
      return this.t(`challenge.counting.${this.countingMethod}.unit`, this.remaining);
    },

    earnedReward() {
      return sellerReward(this.salesTargets, this.seller);
    },

    rewardIsLabel() {
      return !!reachedTier(this.salesTargets, this.seller.score)?.label;
    },

    progressPercent() {
      if (!this.topTier) return 0;
      return Math.min(100, ((this.seller.score || 0) / this.topTier.target) * 100);
    },

    tierGridStyle() {
      return {
        display: "grid",
        gridTemplateColumns: `repeat(${this.sortedTargets.length}, 1fr)`,
      };
    },
  },

  methods: {
    tierReward,

    isReached(tier) {
      return this.seller.score >= tier.target;
    },

    isNext(tier) {
      return this.nextTarget && tier.target === this.nextTarget.target;
    },

    tierTargetClass(tier) {
      if (this.isNext(tier)) return "font-bold text-primary-dark";
      if (this.isReached(tier)) return "font-bold text-gray-900";
      return "font-medium text-gray-400";
    },

    tierRewardClass(tier) {
      if (this.isNext(tier)) return "font-bold text-primary-dark";
      if (this.isReached(tier)) return "font-medium text-gray-500";
      return "font-medium text-gray-400";
    },
  },
};
</script>

<i18n src="./TieredChallengeSellerCard.i18n.json"></i18n>
